import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { canAccessAdmin } from "../utils/permissions";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import {
  FolderOpen, Plus, Lock, LockOpen, PencilSimple, Trash, VideoCamera,
  SpinnerGap, ShieldCheck, UserCircle, Crown, Check, X, CaretDown, Palette
} from "@phosphor-icons/react";

import { API_BASE as API } from "../lib/apiBase";
import ProposerThemesIA from "../components/categories/ProposerThemesIA"; // Tâche 1483
import { getCanonicalRoles } from "../lib/canonicalRoles";
import { useLang } from "../contexts/LanguageContext";

const explainError = (e, fallback = "Erreur inconnue") => {
  if (!e) return fallback;
  const status = e.response?.status;
  const detail = e.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return `${detail} (HTTP ${status || "?"})`;
  if (Array.isArray(detail)) return `Validation: ${detail.map(d => d.msg).join(" · ")} (HTTP 422)`;
  if (e.message?.includes("Network Error")) return "Erreur réseau — vérifie ta connexion";
  return e.message || fallback;
};

// Vincent B554 (Eric 2026-07-25) — ROLE_OPTIONS hardcodé « Employe / Administrateur /
// Vice-President / CEO » remplacé par les vrais rôles LMS canoniques via
// getCanonicalRoles(user, lang), qui utilise les libellés de l'org (role_labels)
// définis dans « Sélection des accès ». Fallback sur les libellés canoniques.
// Les 3 rôles legacy president/ceo/vp sont absorbés par « admin ».

export default function ContentCategoriesPage() {
  // Tâche 1387 — les libellés de rôles suivent la langue de l'écran.
  const { tr, lang } = useLang();
  const { user } = useAuth();
  // Vincent B554 (Eric 2026-07-25) — Les rôles proviennent maintenant des
  // libellés canoniques de l'org (role_labels), triés du moins privilégié au
  // plus privilégié : viewer, employee_new, employee, employee_trained,
  // employee_advanced, concepteur, admin, founder. La forme {id,label} de
  // getCanonicalRoles est mappée en {value,label} pour rester compatible
  // avec le code existant qui utilise `.value`.
  const roleOptions = getCanonicalRoles(user, lang)
    .filter(r => r.id !== "nouveau") // dédoublonnage legacy
    .map(r => ({ value: r.id, label: r.label }));
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingPerms, setEditingPerms] = useState(null);
  // Vincent 2026-05-05 — gérer les vidéos d'une catégorie depuis la vue Catégorie
  const [managingVideosFor, setManagingVideosFor] = useState(null); // {id, name} of category
  const [allVideos, setAllVideos] = useState([]);
  const [videoSaving, setVideoSaving] = useState(null);
  // Bug 534 (Eric 2026-02-14) — 4 modes de tri dans la modale.
  const [videoSort, setVideoSort] = useState("alpha_asc");
  const [allPathways, setAllPathways] = useState([]);
  const [newCat, setNewCat] = useState({ name: "", description: "", color: "#002FA7" });
  const [permRoles, setPermRoles] = useState([]);
  // Vincent B793 (Eric 2026-02-12) — Édition inline du nom d'une catégorie
  // après création. `editingNameFor` = id de la catégorie en cours de
  // renommage. `nameDraft` = texte tapé par l'utilisateur.
  const [editingNameFor, setEditingNameFor] = useState(null);
  const [nameDraft, setNameDraft] = useState("");
  const [renaming, setRenaming] = useState(false);
  // Vincent B553 (Eric 2026-07-25) — Filtre par parcours (à côté du tri).
  // Sous-menu groupé : Parcours d'intégration (onboarding) puis Parcours
  // d'apprentissage (learning), alphabétique. Sélectionner un parcours filtre
  // les catégories qui contiennent au moins 1 vidéo du parcours choisi.
  const [pathwayFilter, setPathwayFilter] = useState(""); // "" = tous, sinon pathway_id
  const [catSort, setCatSort] = useState("alpha_asc");
  const isAdmin = canAccessAdmin(user);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [catsRes, videosRes, pathwaysRes] = await Promise.all([
        axios.get(`${API}/api/content-categories`, { withCredentials: true }),
        axios.get(`${API}/api/videos?limit=500`, { withCredentials: true }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/api/pathways`, { withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      setCategories(catsRes.data);
      setAllVideos(videosRes.data.items || []);
      const pw = pathwaysRes.data?.pathways || pathwaysRes.data?.items || pathwaysRes.data || [];
      setAllPathways(Array.isArray(pw) ? pw : []);
    } catch (e) {
      console.error("[Catégories.load]", e);
      toast.error(explainError(e, tr("Échec du chargement des catégories", "Failed to load the categories")), { duration: 8000 });
    }
    setLoading(false);
  };

  // Vincent B553 — Mapping pathway_id → set(category_id) via les vidéos.
  // Une catégorie apparaît dans le filtre d'un parcours si au moins UNE de ses
  // vidéos appartient à ce parcours (v.category_id ∈ pathway.video_ids).
  const pathwayToCategoryIds = useMemo(() => {
    const map = new Map();
    for (const pw of allPathways) {
      const catIds = new Set();
      for (const vid of (pw.video_ids || [])) {
        const v = allVideos.find(x => x.id === vid);
        if (v?.category_id) catIds.add(v.category_id);
      }
      map.set(pw.id, catIds);
    }
    return map;
  }, [allPathways, allVideos]);

  // Filtrage + tri des catégories affichées
  const displayedCategories = useMemo(() => {
    let out = [...categories];
    if (pathwayFilter) {
      const allowedIds = pathwayToCategoryIds.get(pathwayFilter) || new Set();
      out = out.filter(c => allowedIds.has(c.id));
    }
    if (catSort === "alpha_asc") out.sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr"));
    else if (catSort === "alpha_desc") out.sort((a, b) => (b.name || "").localeCompare(a.name || "", "fr"));
    else if (catSort === "videos_desc") out.sort((a, b) => (b.video_count || 0) - (a.video_count || 0));
    else if (catSort === "videos_asc") out.sort((a, b) => (a.video_count || 0) - (b.video_count || 0));
    return out;
  }, [categories, pathwayFilter, pathwayToCategoryIds, catSort]);

  // Parcours groupés (intégration → apprentissage → hebdomadaire, alphabétique).
  // Vincent B652 (Eric 2026-08-05 SPEC STRICK PROD) — 3e type de parcours.
  const groupedPathways = useMemo(() => {
    const onboarding = allPathways.filter(p => p.pathway_type === "onboarding")
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr"));
    const learning = allPathways.filter(p => p.pathway_type === "learning")
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr"));
    const weekly = allPathways.filter(p => p.pathway_type === "weekly")
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr"));
    return { onboarding, learning, weekly };
  }, [allPathways]);

  const createCategory = async () => {
    if (!newCat.name.trim()) {
      toast.error(tr("Le nom de la catégorie est requis", "The category name is required"));
      return;
    }
    try {
      await axios.post(`${API}/api/content-categories`, newCat, { withCredentials: true });
      toast.success(`Catégorie « ${newCat.name} » créée`);
      setNewCat({ name: "", description: "", color: "#002FA7" });
      setShowCreate(false);
      load();
    } catch (e) {
      console.error("[Catégories.create]", e);
      toast.error(explainError(e, tr("Échec de la création", "Creation failed")), { duration: 8000 });
    }
  };

  const deleteCategory = async (id) => {
    const ok = await confirmDialog(tr("Supprimer cette catégorie ?", "Delete this category?"));
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/content-categories/${id}`, { withCredentials: true });
      toast.success(tr("Catégorie supprimée", "Category deleted"));
      load();
    } catch (e) {
      console.error("[Catégories.delete]", e);
      toast.error(explainError(e, tr("Échec de la suppression", "Deletion failed")), { duration: 8000 });
    }
  };

  const openPermEditor = async (cat) => {
    setEditingPerms(cat.id);
    setPermRoles(cat.allowed_roles || []);
  };

  // Vincent B834 (Eric 2026-02-14 SPEC STRICK PROD) — Édition inline de
  // la couleur d'une catégorie existante. Utilise la même palette que
  // le formulaire de création (6 teintes) + PUT /api/content-categories/:id
  // (le backend accepte `color` en même temps que `name`).
  // Vincent B834 v2 (Eric 2026-02-14 SPEC STRICK PROD) — Palette de 9
  // couleurs pour distinguer les catégories dans l'Explorateur.
  // Vincent B834 v3 (Eric 2026-02-14 SPEC STRICK PROD) — Étendu à 15 teintes
  // pour donner plus de flexibilité (org avec beaucoup de catégories).
  // Les 6 ajouts sont choisis pour maximiser la distance perceptuelle par
  // rapport aux 9 originales (indigo profond, vert forêt, rose fuchsia,
  // orange brûlé, bleu ardoise, jaune moutarde).
  const CATEGORY_COLORS = [
    "#002FA7", "#8b5cf6", "#22c55e", "#f59e0b", "#ef4444",
    "#06b6d4", "#ec4899", "#14b8a6", "#a3a3a3",
    "#4338ca", "#166534", "#d946ef", "#c2410c", "#475569", "#ca8a04",
  ];
  const [colorPickerFor, setColorPickerFor] = useState(null); // catId ouvert
  const [colorSaving, setColorSaving] = useState(false);
  const updateCategoryColor = async (catId, color) => {
    setColorSaving(true);
    try {
      await axios.put(
        `${API}/api/content-categories/${catId}`,
        { color },
        { withCredentials: true },
      );
      toast.success(tr("Couleur mise à jour", "Colour updated"));
      setColorPickerFor(null);
      load();
    } catch (e) {
      console.error("[Catégories.color]", e);
      toast.error(explainError(e, tr("Échec de la mise à jour de la couleur", "Colour update failed")), { duration: 8000 });
    } finally {
      setColorSaving(false);
    }
  };

  // Vincent B834 v4 (Eric 2026-02-14 SPEC STRICK PROD) — Debounce le
  // `<input type="color">` : Chrome fire `onChange` sur chaque pixel de
  // drag → 30+ PUT/seconde sans debounce. On stocke la valeur en cours
  // dans un state local (pour feedback visuel instantané) et on ne
  // PUT qu'après 400ms sans nouveau changement.
  const [customColorDraft, setCustomColorDraft] = useState({}); // { [catId]: "#..." }
  const customColorTimerRef = useRef(null);
  const onCustomColorChange = (catId, color) => {
    setCustomColorDraft((prev) => ({ ...prev, [catId]: color }));
    if (customColorTimerRef.current) clearTimeout(customColorTimerRef.current);
    customColorTimerRef.current = setTimeout(() => {
      updateCategoryColor(catId, color);
    }, 400);
  };

  // Vincent B793 (Eric 2026-02-12) — Renommage inline d'une catégorie.
  const openRenameEditor = (cat) => {
    setEditingNameFor(cat.id);
    setNameDraft(cat.name || "");
  };

  const cancelRename = () => {
    setEditingNameFor(null);
    setNameDraft("");
  };

  const saveRename = async (catId) => {
    const trimmed = (nameDraft || "").trim();
    if (!trimmed) {
      toast.error(tr("Le nom ne peut pas être vide", "The name cannot be empty"));
      return;
    }
    const currentCat = categories.find((c) => c.id === catId);
    if (currentCat && trimmed === currentCat.name) {
      // Rien à faire.
      cancelRename();
      return;
    }
    setRenaming(true);
    try {
      await axios.put(
        `${API}/api/content-categories/${catId}`,
        { name: trimmed },
        { withCredentials: true }
      );
      toast.success(`Catégorie renommée « ${trimmed} »`);
      cancelRename();
      load();
    } catch (e) {
      console.error("[Catégories.rename]", e);
      toast.error(explainError(e, tr("Échec du renommage", "Rename failed")), { duration: 8000 });
    } finally {
      setRenaming(false);
    }
  };

  // Vincent 2026-05-05 — manage which videos belong to this category
  const openVideoManager = async (cat) => {
    try {
      const [videosRes, pathwaysRes] = await Promise.all([
        axios.get(`${API}/api/videos?limit=500`, { withCredentials: true }),
        axios.get(`${API}/api/pathways`, { withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      setAllVideos(videosRes.data.items || []);
      const pw = pathwaysRes.data?.pathways || pathwaysRes.data?.items || pathwaysRes.data || [];
      setAllPathways(Array.isArray(pw) ? pw : []);
      setManagingVideosFor(cat);
    } catch (e) {
      toast.error(explainError(e, tr("Impossible de charger les vidéos", "Could not load the videos")));
    }
  };

  const toggleVideoCategory = async (videoId, currentCatId, targetCatId) => {
    // If video already in target → remove. Otherwise → assign.
    setVideoSaving(videoId);
    try {
      const newCatId = currentCatId === targetCatId ? null : targetCatId;
      await axios.put(
        `${API}/api/videos/${videoId}/category`,
        { category_id: newCatId },
        { withCredentials: true }
      );
      setAllVideos((prev) => prev.map((v) => v.id === videoId ? { ...v, category_id: newCatId } : v));
      toast.success(newCatId ? tr("Vidéo ajoutée", "Video added") : "Vidéo retirée");
      // Also refresh categories to update video_count
      load();
    } catch (e) {
      toast.error(explainError(e, tr("Échec", "Failed")));
    } finally {
      setVideoSaving(null);
    }
  };

  const savePermissions = async (catId) => {
    try {
      // Vincent B566 (Eric 2026-07-27) — `allowed_job_types` a été retiré
      // du modèle Pydantic backend et une migration DB (010) nettoie la
      // clé résiduelle des documents existants.
      await axios.put(`${API}/api/content-categories/${catId}/permissions`, {
        category_id: catId, allowed_roles: permRoles
      }, { withCredentials: true });
      toast.success(tr("Permissions mises à jour", "Permissions updated"));
      setEditingPerms(null);
      load();
    } catch (e) {
      console.error("[Catégories.savePerms]", e);
      toast.error(explainError(e, tr("Échec de la mise à jour des permissions", "Permission update failed")), { duration: 8000 });
    }
  };

  const toggleRole = (role) => setPermRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);

  if (loading) return <div className="flex items-center justify-center h-64"><SpinnerGap size={32} className="animate-spin text-blue-500" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="content-categories-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit']">{tr("Catégories de fichiers", "File categories")}</h1>
          {/* Tâche 1466 (Eric 2026-06) — « quitte à les renommer "Catégories de
            fichiers" dans l'interface pour plus de clarté ». UNE seule liste
            sert les vidéos ET les documents : deux listes auraient été deux
            vérités à tenir à jour. */}
          <p className="text-sm text-zinc-500 mt-1">{tr("Organisez vos vidéos et vos documents par catégories, et gérez les permissions d'accès par rôle", "Organise your videos and documents into categories, and manage access permissions by role")}</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            {/* Tâche 1483 — L'IA propose les thèmes réellement présents dans
                les connaissances : sans catégorie, le quiz ne proposait que
                « Tous les sujets ». */}
            <ProposerThemesIA lang={lang} onApplique={load} />
            <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-500" data-testid="create-category-btn">
              <Plus size={16} /> {tr("Nouvelle catégorie", "New category")}
            </button>
          </div>
        )}
      </div>

      {/* Vincent B553 (Eric 2026-07-25) — Barre filtre + tri au-dessus de la
          grille des catégories. Filtre par parcours (groupé Intégration puis
          Apprentissage, alphabétique) + tri (défaut : alphabétique croissant). */}
      <div className="flex flex-wrap items-center gap-3 pt-1" data-testid="cat-filter-bar">
        <div className="flex items-center gap-2">
          <label htmlFor="cat-filter-pathway" className="text-xs font-medium text-zinc-500">{tr("Filtrer par parcours", "Filter by pathway")}</label>
          <select
            id="cat-filter-pathway"
            value={pathwayFilter}
            onChange={e => setPathwayFilter(e.target.value)}
            className="h-9 px-3 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-900"
            data-testid="cat-filter-pathway-select"
          >
            <option value="">{tr("Tous les parcours", "All pathways")}</option>
            {groupedPathways.onboarding.length > 0 && (
              <optgroup label={tr("Parcours d'intégration", "Onboarding pathways")}>
                {groupedPathways.onboarding.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </optgroup>
            )}
            {groupedPathways.learning.length > 0 && (
              <optgroup label={tr("Parcours d'apprentissage", "Learning pathways")}>
                {groupedPathways.learning.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </optgroup>
            )}
            {/* Vincent B652 (Eric 2026-08-05 SPEC STRICK PROD) */}
            {groupedPathways.weekly.length > 0 && (
              <optgroup label={tr("Formations hebdomadaires", "Weekly training")}>
                {groupedPathways.weekly.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </optgroup>
            )}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="cat-sort" className="text-xs font-medium text-zinc-500">{tr("Trier", "Sort")}</label>
          <select
            id="cat-sort"
            value={catSort}
            onChange={e => setCatSort(e.target.value)}
            className="h-9 px-3 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-900"
            data-testid="cat-sort-select"
          >
            <option value="alpha_asc">{tr("Alphabétique (A → Z)", "Alphabetical (A → Z)")}</option>
            <option value="alpha_desc">{tr("Alphabétique (Z → A)", "Alphabetical (Z → A)")}</option>
            <option value="videos_desc">{tr("Nombre de vidéos (+)", "Video count (+)")}</option>
            <option value="videos_asc">{tr("Nombre de vidéos (-)", "Video count (−)")}</option>
          </select>
        </div>
        <div className="text-xs text-zinc-500 ml-auto" data-testid="cat-count">
          {lang === "fr"
            ? `${displayedCategories.length} catégorie${displayedCategories.length !== 1 ? "s" : ""}`
              + (pathwayFilter ? ` filtrée${displayedCategories.length !== 1 ? "s" : ""} sur ${categories.length}` : "")
            : `${displayedCategories.length} categor${displayedCategories.length !== 1 ? "ies" : "y"}`
              + (pathwayFilter ? ` filtered out of ${categories.length}` : "")}
        </div>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="bg-card border border-border rounded-xl p-5" data-testid="create-category-form">
          <h3 className="font-semibold mb-4">{tr("Créer une catégorie", "Create a category")}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs font-medium text-zinc-500 block mb-1">{tr("Nom", "Name")}</label>
              <input value={newCat.name} onChange={e => setNewCat({...newCat, name: e.target.value})} placeholder={tr("Ex: Formation initiale", "e.g. Initial training")} className="w-full h-10 px-3 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-transparent" data-testid="cat-name-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500 block mb-1">{tr("Couleur", "Colour")}</label>
              <div className="flex gap-2">
                {["#002FA7", "#8b5cf6", "#22c55e", "#f59e0b", "#ef4444", "#06b6d4"].map(c => (
                  <button key={c} onClick={() => setNewCat({...newCat, color: c})} className={`w-8 h-8 rounded-lg border-2 ${newCat.color === c ? "border-zinc-900 dark:border-white" : "border-transparent"}`} style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>
          </div>
          <div className="mb-4">
            <label className="text-xs font-medium text-zinc-500 block mb-1">{tr("Description", "Description")}</label>
            <input value={newCat.description} onChange={e => setNewCat({...newCat, description: e.target.value})} placeholder={tr("Description optionnelle", "Optional description")} className="w-full h-10 px-3 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-transparent" data-testid="cat-desc-input" />
          </div>
          <div className="flex gap-2">
            <button onClick={createCategory} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-500" data-testid="save-cat-btn">{tr("Créer", "Create")}</button>
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-zinc-500 text-sm">{tr("Annuler", "Cancel")}</button>
          </div>
        </div>
      )}

      {/* Catégories Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="categories-grid">
        {displayedCategories.map(cat => (
          <div key={cat.id} className={`bg-card border rounded-xl overflow-hidden ${cat.locked ? "border-red-200 dark:border-red-800 opacity-75" : (managingVideosFor?.id === cat.id ? "border-[#002FA7] ring-2 ring-[#002FA7]/20" : "border-border")}`} data-testid={`category-${cat.id}`}>
            {/* Vincent B834 v2 (Eric 2026-02-14 SPEC STRICK PROD) — Bandeau
                supérieur épais qui reflète la couleur active. Non-cliquable
                (l'édition se fait via la pastille dans l'en-tête, plus
                découvrable). */}
            <div
              className="h-1.5 w-full"
              style={{ backgroundColor: cat.color || "#002FA7" }}
              data-testid={`cat-color-band-${cat.id}`}
              aria-hidden="true"
            />
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {/* Vincent B834 v2 — Pastille ronde cliquable style Notion
                      pour changer la couleur de la catégorie. Icône Palette
                      au survol pour signaler la cliquabilité. */}
                  {isAdmin ? (
                    <button
                      type="button"
                      onClick={() => setColorPickerFor(colorPickerFor === cat.id ? null : cat.id)}
                      className="group relative w-6 h-6 rounded-full flex-shrink-0 border-2 border-white dark:border-zinc-800 shadow ring-1 ring-zinc-200 dark:ring-zinc-700 hover:ring-zinc-400 dark:hover:ring-zinc-500 hover:scale-110 transition-all"
                      style={{ backgroundColor: cat.color || "#002FA7" }}
                      title={tr("Cliquez pour changer la couleur", "Click to change the colour")}
                      aria-label={tr("Changer la couleur de la catégorie", "Change the category colour")}
                      data-testid={`cat-color-pill-${cat.id}`}
                    >
                      <Palette
                        size={12}
                        weight="fill"
                        className="absolute inset-0 m-auto text-white opacity-0 group-hover:opacity-90 transition-opacity drop-shadow"
                      />
                    </button>
                  ) : (
                    <span
                      className="w-6 h-6 rounded-full flex-shrink-0 border-2 border-white dark:border-zinc-800 shadow ring-1 ring-zinc-200 dark:ring-zinc-700"
                      style={{ backgroundColor: cat.color || "#002FA7" }}
                      data-testid={`cat-color-pill-${cat.id}`}
                      aria-hidden="true"
                    />
                  )}
                  {cat.locked ? <Lock size={16} className="text-red-500 flex-shrink-0" /> : <FolderOpen size={16} className="text-blue-500 flex-shrink-0" />}
                  {/* Vincent B793 (Eric 2026-02-12) — Nom éditable inline. */}
                  {editingNameFor === cat.id ? (
                    <div className="flex items-center gap-1 flex-1 min-w-0" data-testid={`cat-name-editor-${cat.id}`}>
                      <input
                        type="text"
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveRename(cat.id);
                          else if (e.key === "Escape") cancelRename();
                        }}
                        disabled={renaming}
                        ref={(el) => { if (el && editingNameFor === cat.id && document.activeElement !== el) el.focus(); }}
                        className="flex-1 min-w-0 h-7 px-2 text-sm font-semibold border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-zinc-800"
                        data-testid={`cat-name-input-${cat.id}`}
                        maxLength={80}
                      />
                      <button
                        onClick={() => saveRename(cat.id)}
                        disabled={renaming}
                        className="p-1 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded disabled:opacity-50"
                        title={tr("Enregistrer (Entrée)", "Save (Enter)")}
                        aria-label={tr("Enregistrer le nom", "Save the name")}
                        data-testid={`cat-name-save-${cat.id}`}
                      >
                        <Check size={14} weight="bold" />
                      </button>
                      <button
                        onClick={cancelRename}
                        disabled={renaming}
                        className="p-1 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded disabled:opacity-50"
                        title={tr("Annuler (Échap)", "Cancel (Esc)")}
                        aria-label={tr("Annuler le renommage", "Cancel the rename")}
                        data-testid={`cat-name-cancel-${cat.id}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <h3 className="font-semibold text-sm truncate">{cat.name}</h3>
                  )}
                </div>
                <span className="text-xs text-zinc-500 flex-shrink-0">
                  {lang === "fr"
                    ? `${cat.video_count} vidéo${cat.video_count !== 1 ? "s" : ""}`
                    : `${cat.video_count} video${cat.video_count !== 1 ? "s" : ""}`}
                </span>
              </div>
              {cat.description && <p className="text-xs text-zinc-500 mb-3">{cat.description}</p>}

              {/* Vincent B834 v2 — Palette inline qui s'ouvre au clic sur la
                  pastille. Affiche les 15 teintes (v3) + input couleur
                  personnalisée (v4) + fermeture. */}
              {isAdmin && colorPickerFor === cat.id && (
                <div
                  className="mb-3 px-2 py-2 flex flex-wrap items-center gap-2 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg border border-zinc-200 dark:border-zinc-700"
                  data-testid={`cat-color-picker-${cat.id}`}
                >
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1">{tr("Couleur :", "Colour:")}</span>
                  {CATEGORY_COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => updateCategoryColor(cat.id, c)}
                      disabled={colorSaving}
                      className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 disabled:opacity-50 ${cat.color === c ? "border-zinc-900 dark:border-white ring-2 ring-offset-1 ring-zinc-900 dark:ring-white" : "border-white dark:border-zinc-800 shadow"}`}
                      style={{ backgroundColor: c }}
                      title={c}
                      data-testid={`cat-color-swatch-${cat.id}-${c.replace('#', '')}`}
                      aria-label={`${tr("Couleur", "Colour")} ${c}`}
                    />
                  ))}
                  {/* Vincent B834 v4 (Eric 2026-02-14 SPEC STRICK PROD) — Input
                      couleur personnalisée : permet à l'admin de choisir une
                      teinte HORS des 15 preset. Le natif <input type="color">
                      ouvre la palette système. On surligne l'input avec un
                      cadre pointillé pour signaler qu'il est custom (vs les
                      swatches à ring solide quand actifs). L'onChange est
                      debouncé (400ms) pour éviter 30 PUT/seconde. */}
                  {(() => {
                    const _currentColor = (customColorDraft[cat.id] || cat.color || "").toLowerCase();
                    const _isCustom = _currentColor && !CATEGORY_COLORS.includes(_currentColor);
                    return (
                      <label
                        className={`relative w-6 h-6 rounded-full cursor-pointer transition-transform hover:scale-110 ${_isCustom ? "ring-2 ring-offset-1 ring-zinc-900 dark:ring-white" : ""}`}
                        style={{
                          background: _isCustom
                            ? _currentColor
                            : "conic-gradient(from 180deg, #ef4444, #f59e0b, #22c55e, #06b6d4, #8b5cf6, #ec4899, #ef4444)",
                          border: "2px solid white",
                          boxShadow: "0 0 0 1px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.15)",
                        }}
                        title={tr("Couleur personnalisée", "Custom colour")}
                        aria-label={tr("Choisir une couleur personnalisée", "Pick a custom colour")}
                        data-testid={`cat-color-custom-${cat.id}`}
                      >
                        <input
                          type="color"
                          value={customColorDraft[cat.id] || cat.color || "#002FA7"}
                          onChange={(e) => onCustomColorChange(cat.id, e.target.value.toLowerCase())}
                          disabled={colorSaving}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                          data-testid={`cat-color-custom-input-${cat.id}`}
                        />
                        {/* Petit « + » central visible seulement si la couleur
                            actuelle EST déjà une preset (sinon la teinte custom
                            remplit le cercle). */}
                        {!_isCustom && (
                          <span className="absolute inset-0 flex items-center justify-center text-white text-[10px] font-bold pointer-events-none drop-shadow">
                            +
                          </span>
                        )}
                      </label>
                    );
                  })()}
                  <button
                    onClick={() => setColorPickerFor(null)}
                    disabled={colorSaving}
                    className="ml-auto p-1 text-zinc-400 hover:text-zinc-700"
                    title={tr("Fermer", "Close")}
                    aria-label={tr("Fermer le sélecteur de couleur", "Close the colour picker")}
                    data-testid={`cat-color-close-${cat.id}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              )}

              {/* Permission badges — Vincent B566 : job_types retirés */}
              <div className="flex flex-wrap gap-1 mb-3">
                {cat.allowed_roles?.length > 0 ? cat.allowed_roles.map(r => (
                  <span key={r} className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded">
                    {roleOptions.find(o => o.value === r)?.label || r}
                  </span>
                )) : null}
                {!cat.allowed_roles?.length && (
                  <span className="px-1.5 py-0.5 text-[10px] font-medium bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 rounded flex items-center gap-1">
                    <LockOpen size={10} /> {tr("Accès libre", "Open access")}
                  </span>
                )}
              </div>

              {/* Admin actions */}
              {isAdmin && (
                <div className="flex gap-2 pt-2 border-t border-border">
                  <button onClick={() => managingVideosFor?.id === cat.id ? setManagingVideosFor(null) : openVideoManager(cat)} className={`flex-1 flex items-center justify-center gap-1 py-1.5 text-xs font-medium rounded-lg transition-colors ${managingVideosFor?.id === cat.id ? "bg-[#002FA7] text-white" : "text-teal-700 hover:bg-teal-50 dark:hover:bg-teal-900/20"}`} data-testid={`videos-btn-${cat.id}`}>
                    <VideoCamera size={14} /> {tr("Vidéos", "Videos")}
                  </button>
                  {/* Vincent B793 (Eric 2026-02-12) — Bouton Renommer. */}
                  <button
                    onClick={() => openRenameEditor(cat)}
                    className="flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors"
                    data-testid={`rename-cat-${cat.id}`}
                    title={tr("Renommer la catégorie", "Rename the category")}
                    aria-label={tr("Renommer la catégorie", "Rename the category")}
                  >
                    <PencilSimple size={14} /> {tr("Renommer", "Rename")}
                  </button>
                  <button onClick={() => openPermEditor(cat)} className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" data-testid={`perms-btn-${cat.id}`}>
                    <ShieldCheck size={14} /> {tr("Permissions", "Permissions")}
                  </button>
                  <button onClick={() => deleteCategory(cat.id)} className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" data-testid={`delete-cat-${cat.id}`}>
                    <Trash size={14} />
                  </button>
                </div>
              )}

              {/* Permission Éditeur */}
              {editingPerms === cat.id && (
                <div className="mt-3 pt-3 border-t border-border" data-testid={`perm-editor-${cat.id}`}>
                  <h4 className="text-xs font-bold mb-2">{tr("Rôles autorisés", "Allowed roles")}</h4>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {roleOptions.map(r => (
                      <button key={r.value} onClick={() => toggleRole(r.value)} className={`px-2 py-1 text-[11px] font-medium rounded-lg border transition-colors ${permRoles.includes(r.value) ? "bg-blue-600 text-white border-blue-600" : "border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400"}`}>
                        {r.label}
                      </button>
                    ))}
                  </div>
                  {/* Vincent (Eric 2026-07-27) — INVERSION du défaut :
                      catégorie fermée par défaut. Aucun rôle coché →
                      verrouillée pour les non-admins (admins/CEO/président
                      gardent l'accès total automatiquement). */}
                  <p className="text-[10px] text-amber-600 dark:text-amber-400 mb-2 flex items-center gap-1">
                    <span aria-hidden="true">🔒</span>
                    <span>{tr("Aucun rôle coché = catégorie verrouillée (sauf admins/CEO/président)", "No role ticked = category locked (admins/CEO/president aside)")}</span>
                  </p>
                  <div className="flex gap-2">
                    <button onClick={() => savePermissions(cat.id)} className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg" data-testid={`save-perms-${cat.id}`}>
                      <Check size={12} className="inline mr-1" />{tr("Enregistrer", "Save")}
                    </button>
                    <button onClick={() => setEditingPerms(null)} className="px-3 py-1.5 text-xs text-zinc-500">{tr("Annuler", "Cancel")}</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {displayedCategories.length === 0 && categories.length > 0 && pathwayFilter && (
        <div className="text-center py-16" data-testid="cat-empty-filtered">
          <FolderOpen size={48} className="mx-auto mb-4 text-zinc-300" />
          <h3 className="font-medium text-lg">{tr("Aucune catégorie pour ce parcours", "No category for this pathway")}</h3>
          <p className="text-sm text-zinc-500 mt-1">
            {lang === "fr"
              ? `Aucune catégorie ne contient de vidéo appartenant à « ${allPathways.find(p => p.id === pathwayFilter)?.name || "ce parcours"} ».`
              : `No category holds a video belonging to "${allPathways.find(p => p.id === pathwayFilter)?.name || "this pathway"}".`}
          </p>
          <button onClick={() => setPathwayFilter("")} className="mt-4 text-sm text-blue-600 hover:underline" data-testid="cat-clear-filter-btn">{tr("Réinitialiser le filtre", "Reset the filter")}</button>
        </div>
      )}

      {categories.length === 0 && (
        <div className="text-center py-16">
          <FolderOpen size={48} className="mx-auto mb-4 text-zinc-300" />
          <h3 className="font-medium text-lg">{tr("Aucune catégorie", "No category")}</h3>
          <p className="text-sm text-zinc-500 mt-1">{tr("Créez des catégories pour organiser vos vidéos et gérer les permissions d'accès.", "Create categories to organise your videos and manage access permissions.")}</p>
        </div>
      )}

      {/* Vincent B546 v2 (Eric 2026-07-24) — Liste inline SOUS la grille (au
          lieu d'une fenêtre contextuelle), comme demandé initialement.
          Vincent : « au lieu de les mettre dans une fenêtre contextuelle,
          tu vas afficher cette liste en dessous des tuiles ». La tuile
          sélectionnée reçoit un ring bleu Knoria pour indiquer le lien
          visuel entre la tuile et la liste ouverte. */}
      {managingVideosFor && (
        <div
          className="bg-card border border-[#002FA7]/30 rounded-2xl shadow-sm mt-2 overflow-hidden animate-fade-in"
          data-testid="cat-videos-inline"
        >
          <div className="flex items-center justify-between p-4 border-b border-border bg-gradient-to-r from-[#002FA7]/5 to-transparent">
            <div className="flex items-center gap-3 min-w-0">
              <FolderOpen size={20} style={{ color: managingVideosFor.color || "#002FA7" }} className="flex-shrink-0" />
              <div className="min-w-0">
                <h3 className="text-base font-semibold truncate">
                  {lang === "fr"
                    ? `Vidéos de « ${managingVideosFor.name} »`
                    : `Videos in "${managingVideosFor.name}"`}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {tr(
                    "Cliquez sur une vidéo pour l'ajouter (ou la retirer) de cette catégorie. Les tris alphabétiques par parcours s'appliquent sur les parcours d'intégration et d'apprentissage déjà définis dans la plateforme.",
                    "Click a video to add it to (or remove it from) this category. The by-pathway alphabetical sorts apply to the onboarding and learning pathways already defined in the platform.",
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={() => setManagingVideosFor(null)}
              className="p-2 hover:bg-muted rounded-lg transition-colors flex-shrink-0"
              data-testid="cat-videos-inline-close"
            >
              <X size={18} />
            </button>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-muted/30">
            <label className="text-xs font-medium text-zinc-600 dark:text-zinc-300 flex-shrink-0">
              {tr("Trier par :", "Sort by:")}
            </label>
            <select
              value={videoSort}
              onChange={(e) => setVideoSort(e.target.value)}
              className="text-xs h-8 px-2 border border-zinc-200 dark:border-zinc-700 rounded-md bg-transparent"
              data-testid="cat-videos-sort-select"
            >
              <option value="alpha_asc">{tr("Alphabétique tous (A → Z)", "Alphabetical, all (A → Z)")}</option>
              <option value="alpha_desc">{tr("Alphabétique tous (Z → A)", "Alphabetical, all (Z → A)")}</option>
              <option value="pathway_asc">{tr("Alphabétique par parcours (A → Z)", "Alphabetical by pathway (A → Z)")}</option>
              <option value="pathway_desc">{tr("Alphabétique par parcours (Z → A)", "Alphabetical by pathway (Z → A)")}</option>
            </select>
          </div>
          <div className="max-h-[60vh] overflow-y-auto p-4 space-y-2">
            {allVideos.length === 0 ? (
              <p className="text-center text-sm text-zinc-500 py-8">{tr("Aucune vidéo dans cette organisation.", "No video in this organisation.")}</p>
            ) : (
              (() => {
                // Vincent B546 v2 (Eric 2026-07-24) — Le lien vidéo↔parcours
                // est stocké dans `pathway.video_ids` (côté pathway), PAS
                // dans `video.pathway_ids` (qui n'existe pas). D'où l'ancien
                // « Sans parcours » sur toutes les vidéos. On construit un
                // reverse-map ici pour retrouver les parcours d'une vidéo.
                const pathwayNamesByVideoId = {};
                for (const p of allPathways) {
                  const name = p?.name || "";
                  if (!name) continue;
                  const vids = Array.isArray(p?.video_ids) ? p.video_ids : [];
                  for (const vid of vids) {
                    if (!pathwayNamesByVideoId[vid]) pathwayNamesByVideoId[vid] = [];
                    pathwayNamesByVideoId[vid].push(name);
                  }
                }
                const pathwayNameFor = (video) => {
                  const names = pathwayNamesByVideoId[video.id];
                  // `\uffff` force ce groupe en queue d'alphabet : c'est une CLÉ
                  // DE TRI, pas un libellé. Elle reste en français et n'est
                  // traduite qu'à l'affichage (sinon l'ordre changerait avec
                  // la langue).
                  if (!names || names.length === 0) return "\uffff Sans parcours";
                  return [...names].sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }))[0];
                };
                const cmpTitle = (a, b) =>
                  (a.title || "").localeCompare(b.title || "", "fr", { sensitivity: "base" });
                const sorted = [...allVideos];
                if (videoSort === "alpha_asc") sorted.sort(cmpTitle);
                else if (videoSort === "alpha_desc") sorted.sort((a, b) => -cmpTitle(a, b));
                else if (videoSort === "pathway_asc") {
                  sorted.sort((a, b) => {
                    const cmp = pathwayNameFor(a).localeCompare(pathwayNameFor(b), "fr", { sensitivity: "base" });
                    return cmp !== 0 ? cmp : cmpTitle(a, b);
                  });
                } else if (videoSort === "pathway_desc") {
                  sorted.sort((a, b) => {
                    const cmp = pathwayNameFor(b).localeCompare(pathwayNameFor(a), "fr", { sensitivity: "base" });
                    return cmp !== 0 ? cmp : cmpTitle(a, b);
                  });
                }
                const showPathwayGroups = videoSort === "pathway_asc" || videoSort === "pathway_desc";
                let prevPathwayName = null;
                return sorted.map((v) => {
                  const isInThisCat = v.category_id === managingVideosFor.id;
                  const isInOtherCat = v.category_id && !isInThisCat;
                  const otherCatName = isInOtherCat ? (categories.find((c) => c.id === v.category_id)?.name || "?") : null;
                  const currentPathwayNameRaw = pathwayNameFor(v).replace(/^\uffff /, "");
                  const currentPathwayName = currentPathwayNameRaw === "Sans parcours"
                    ? tr("Sans parcours", "No pathway")
                    : currentPathwayNameRaw;
                  const showHeader = showPathwayGroups && currentPathwayName !== prevPathwayName;
                  if (showHeader) prevPathwayName = currentPathwayName;
                  return (
                    <div key={v.id}>
                      {showHeader && (
                        <div
                          className="px-1 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400"
                          data-testid={`cat-videos-pathway-header-${currentPathwayName.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          {currentPathwayName}
                        </div>
                      )}
                      <button
                        onClick={() => toggleVideoCategory(v.id, v.category_id, managingVideosFor.id)}
                        disabled={videoSaving === v.id}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          isInThisCat
                            ? "bg-teal-50 border-teal-300 hover:bg-teal-100"
                            : "bg-card border-border hover:bg-muted"
                        } disabled:opacity-50`}
                        data-testid={`cat-video-row-${v.id}`}
                      >
                        <div className="flex items-center gap-3">
                          {isInThisCat ? (
                            <div className="w-5 h-5 rounded-full bg-teal-500 text-white flex items-center justify-center flex-shrink-0">
                              <Check size={12} weight="bold" />
                            </div>
                          ) : (
                            <div className="w-5 h-5 rounded-full border-2 border-zinc-300 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{v.title || tr("(sans titre)", "(untitled)")}</p>
                            <p className="text-[11px] text-zinc-500 mt-0.5">
                              {v.pipeline_status === "complete"
                                ? tr("✓ Pipeline terminé", "✓ Pipeline complete")
                                : `⏳ ${v.pipeline_status}`}
                              {isInOtherCat && (
                                <span className="ml-2 text-amber-700">
                                  {lang === "fr"
                                    ? ` · actuellement dans : ${otherCatName} (clic = transférer)`
                                    : ` · currently in: ${otherCatName} (click = move)`}
                                </span>
                              )}
                            </p>
                          </div>
                          {videoSaving === v.id && <SpinnerGap size={16} className="animate-spin text-zinc-400" />}
                        </div>
                      </button>
                    </div>
                  );
                });
              })()
            )}
          </div>
          <div className="p-4 border-t border-border bg-muted/30 text-xs text-zinc-600 text-center">
            {lang === "fr"
              ? `${allVideos.filter((v) => v.category_id === managingVideosFor.id).length} vidéo(s) dans cette catégorie · ${allVideos.length} au total`
              : `${allVideos.filter((v) => v.category_id === managingVideosFor.id).length} video(s) in this category · ${allVideos.length} in total`}
          </div>
        </div>
      )}
    </div>
  );
}
